import React from 'react';
import { Flex, Box, Text, Switch, IconButton } from '@chakra-ui/react';
import { PageBreadcrumb } from './UI/PageBreadcrumb';
import { CustomButton } from './UI/CustomButton';
import { MdArrowBack, MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';

interface BreadcrumbItem {
  label: string;
  isClickable: boolean;
  isCurrent: boolean;
}

interface ProgramTopBarProps {
  breadcrumbs: BreadcrumbItem[];
  onCancel: () => void;
  onSave: (formData?: any) => void;
  onBack: () => void;
  onBreadcrumbClick?: (idx: number) => void;
  isPublished: boolean;
  setIsPublished: (isPublished: boolean) => void;
  selectedNode: any | null;
  formData?: any;
  isExerciseManagement?: boolean;
  // New props
  onGeneralSave?: () => void;
  isSaving?: boolean;
}

const ProgramTopBar: React.FC<ProgramTopBarProps> = ({ breadcrumbs, onCancel, onSave, onBack, onBreadcrumbClick, isPublished, setIsPublished, selectedNode, formData, isExerciseManagement, onGeneralSave, isSaving }) => {
  if (!selectedNode) {
    // Program-level top bar with breadcrumbs
    return (
      <Flex align="center" justify="flex-end" p={4} marginBottom='-58px'>
        <Box display="flex" alignItems="center" gap={2}>
          <Flex align="center" gap={2}>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              marginRight='30px'
            >
              <Box
                position="relative"
                display="inline-flex"
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                  setIsPublished(!isPublished);
                }}
              >
                {/* Switch Track */}
                <Box
                  w="20px"
                  h="10px"
                  bg={isPublished ? "#5FCBA3" : "#FC3C5E"}
                  borderRadius="5px"
                  position="relative"
                  transition="background-color 0.2s"
                />
                {/* Switch Thumb */}
                <Box
                  position="absolute"
                  left={isPublished ? "12px" : "2px"}
                  top="2px"
                  w="6px"
                  h="6px"
                  bg="white"
                  borderRadius="50%"
                  transition="left 0.2s"
                />
              </Box>
              <Text color='gray.500' fontSize="sm">
                {isPublished ? 'Published' : 'Unpublished'}
              </Text>
            </Box>
            <CustomButton onClick={onCancel} visual="subtle" mr={2} h='30px'>
              Cancel
            </CustomButton>
            <CustomButton onClick={onSave} visual="solid" h='30px'>
              Save
            </CustomButton>
          </Flex>
        </Box>
      </Flex>
    );
  } else {
    // Step/Step Option/Exercise top bar with breadcrumbs
    return (
      <Flex align="center" justify="space-between" p={4}>
        <Flex align="center" gap={3}>
          {/* Back button */}
          <CustomButton
            aria-label="Go back"
            onClick={onBack}
            borderColor="gray.300"
            color="gray.600"
            mr='20px'
            w='56px'
            h='48px'
            border='1px solid #DADADA'
            visual="ghost"
          >
            <MdArrowBack />
          </CustomButton>
          {/* Breadcrumbs */}
          <Flex align="center" gap='16px'>
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                <Text
                  color={crumb.isClickable ? '#383838' : '#8b8b8b'}
                  textDecoration={crumb.isClickable ? 'underline' : 'none'}
                  cursor={crumb.isClickable ? 'pointer' : 'default'}
                  fontSize='16px'
                  onClick={() => crumb.isClickable && onBreadcrumbClick && onBreadcrumbClick(idx)}
                  _hover={crumb.isClickable ? { color: 'blue.600' } : {}}
                >
                  {crumb.label}
                </Text>
                {idx < breadcrumbs.length - 1 && (
                  <MdArrowForwardIos color='#8b8b8b' />
                )}
              </React.Fragment>
            ))}
          </Flex>
        </Flex>
        <Box display="flex" alignItems="center" gap={2}>
          <CustomButton onClick={onCancel} visual="subtle" mr={2} h='30px'>
            Cancel
          </CustomButton>
          <CustomButton onClick={() => onSave(formData)} visual="solid" h='30px'>
            {isExerciseManagement ? 'Save Exercise Option' : 'Save Step Option'}
          </CustomButton>
          {onGeneralSave && (
            <CustomButton onClick={onGeneralSave} visual="solid" h='30px' ml={2} loading={isSaving}>
              Save Program
            </CustomButton>
          )}
        </Box>
      </Flex>
    );
  }
};

export default ProgramTopBar; 